import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from './auth.ts';
import { getUser, setUser as saveUser, clearAuth as clearStoredAuth } from './auth.ts';
import { gateJustHandled } from './plan.ts';

// Safari restricts crypto.randomUUID to secure contexts (HTTPS or localhost).
// Toast IDs only need to be unique within the session, so fall back to a
// timestamp + Math.random when the API isn't available (e.g. http://<lan-ip>).
const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export interface ChatMessage {
  role: 'user' | 'coach';
  content: string;
  // API ``ai_sources`` of a coach answer: ["garmin_connect"] → Garmin attribution.
  aiSources?: string[];
}

export interface Toast {
  id: string;
  message: string;
  type: 'error' | 'success';
}

interface AppState {
  // Auth
  user: User | null;
  setUser: (u: User) => void;
  clearUser: () => void;

  // Chat
  ftp: number | null;
  setFTP: (ftp: number) => void;
  sessionId: string | null;
  setSessionId: (id: string) => void;
  conversationId: number | null;
  setConversationId: (id: number | null) => void;
  chatHistory: ChatMessage[];
  addMessage: (msg: ChatMessage) => void;
  updateLastMessage: (content: string) => void;
  setLastMessageSources: (sources: string[]) => void;
  clearChat: () => void;

  // Toasts
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      // Auth
      user: getUser(),
      setUser: (u) => {
        saveUser(u);
        set({ user: u });
      },
      // Also drops the persisted chat, so the next account signed in on
      // this browser doesn't see (or continue) the previous user's chat.
      clearUser: () => {
        clearStoredAuth();
        set({ user: null, ftp: null, chatHistory: [], sessionId: null, conversationId: null });
      },

      // Chat
      ftp: null,
      setFTP: (ftp) => set({ ftp }),
      sessionId: null,
      setSessionId: (id) => set({ sessionId: id }),
      conversationId: null,
      setConversationId: (id) => set({ conversationId: id }),
      chatHistory: [],
      addMessage: (msg) =>
        set((s) => ({ chatHistory: [...s.chatHistory, msg] })),
      updateLastMessage: (content) =>
        set((s) => {
          const history = [...s.chatHistory];
          const last = history.at(-1);
          if (last) {
            history[history.length - 1] = { ...last, content };
          }
          return { chatHistory: history };
        }),
      setLastMessageSources: (sources) =>
        set((s) => {
          const history = [...s.chatHistory];
          const last = history.at(-1);
          if (last?.role === 'coach') {
            history[history.length - 1] = { ...last, aiSources: sources };
          }
          return { chatHistory: history };
        }),
      clearChat: () => set({ chatHistory: [], sessionId: null, conversationId: null }),

      // Toasts
      toasts: [],
      addToast: (toast) => {
        // The paywall / daily-limit message already covers this failure.
        if (toast.type === 'error' && gateJustHandled()) return;
        set((s) => ({
          toasts: [...s.toasts, { ...toast, id: newId() }],
        }));
      },
      removeToast: (id) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    }),
    {
      name: 'cycling-coach',
      partialize: (state) => ({
        ftp: state.ftp,
        sessionId: state.sessionId,
        conversationId: state.conversationId,
        chatHistory: state.chatHistory,
      }),
    },
  ),
);
